import React, { useState } from 'react';
import { Icon } from './ui/Icon.jsx';
import T from '../locales/translations.js';
import { uid } from '../lib/utils.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { useCustomers, useSaveCustomer, useDeleteCustomer } from '../hooks/useCustomers.js';
import { useCustomerTokenIndex } from '../hooks/useCustomerTokenIndex.js';
import { ViewAsCustomerLink } from './admin/ViewAsCustomerLink.jsx';
import { useAllOrders } from '../hooks/useOrders.js';
import { getValidToken } from '../lib/supabase.js';
import { SUPABASE_URL, SUPABASE_KEY } from '../lib/constants.js';

// ─── CUSTOMERS ────────────────────────────────────────────────────────────────
export default function Customers({ lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();

  // ── data + mutations ──
  const { data: customers = [], isLoading } = useCustomers();
  // CRM rows have no FK to the accounts that own catalogue tokens — resolve the
  // link by email/phone so "View as customer" works here too. One query total.
  const { findAccount } = useCustomerTokenIndex();
  const saveCustomer   = useSaveCustomer();
  const deleteCustomer = useDeleteCustomer();

  // Fetch all orders for the inline order-history panel.
  // Only triggered once at least one history panel is opened.
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const { data: orders = [] } = useAllOrders({ enabled: historyEnabled });

  // ── local UI state ──
  const [form, setForm]                   = useState(null);
  const [msg, setMsg]                     = useState('');
  const [search, setSearch]               = useState('');
  const [expandedHistory, setExpandedHistory] = useState(null); // customer id

  // ── newsletter / announcement state ──
  const [annModal, setAnnModal]     = useState(false);
  const [annSubject, setAnnSubject] = useState('');
  const [annBody, setAnnBody]       = useState('');
  const [annSending, setAnnSending] = useState(false);

  const blank = { id: uid(), business_name: '', contact_name: '', phone: '', whatsapp: '', email: '', address: '', notes: '', source: 'manual' };
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const btnStyle = (c = 'var(--sedra-olive)') => ({ padding: '7px 13px', borderRadius: 7, background: c, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' });

  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2000); };

  // ── newsletter recipients: anyone with a valid email and not explicitly unsubscribed ──
  const recipientEmails = customers.filter(c => c.email && c.is_subscribed !== false).map(c => c.email);
  const recipientCount  = recipientEmails.length;

  // ── save (add / edit) ──
  const save = async () => {
    if (!form.contact_name && !form.business_name) return;
    try {
      await saveCustomer.mutateAsync(form);
      setForm(null); toast('Saved!');
    } catch (e) { toast('Error: ' + e.message); }
  };

  // ── delete ──
  const del = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try { await deleteCustomer.mutateAsync(id); } catch {}
  };

  // ── WhatsApp quick-message ──
  const whatsappCustomer = (c) => {
    const rawPhone = (c.whatsapp || c.phone || '').replace(/\D/g,'');
    if (!rawPhone) return;
    const intlPhone = rawPhone.startsWith('20') ? rawPhone : rawPhone.startsWith('0') ? '20' + rawPhone.slice(1) : '20' + rawPhone;
    window.open(`https://wa.me/${intlPhone}`);
  };

  // ── export CSV ──
  const exportCustomers = () => {
    const rows = [['Business','Contact','Phone','WhatsApp','Email','Address','Source','Last Order','Notes']];
    customers.forEach(c => rows.push([c.business_name||'', c.contact_name||'', c.phone||'', c.whatsapp||'', c.email||'', c.address||'', c.source||'manual', c.last_order_at?.slice(0,10)||'', c.notes||'']));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,﻿' + encodeURIComponent(csv);
    a.download = `sedra_customers_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── send mass announcement ──
  const sendAnnouncement = async () => {
    if (!annSubject.trim() || !annBody.trim() || recipientEmails.length === 0) return;
    setAnnSending(true);
    try {
      const authToken = await getValidToken();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-announcement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ subject: annSubject, body: annBody, emails: recipientEmails }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Send failed');
      }
      setAnnModal(false);
      setAnnSubject('');
      setAnnBody('');
      toast(lang === 'ar' ? 'تم إرسال الإعلان بنجاح!' : 'Announcement sent!');
    } catch (e) {
      toast('Error: ' + e.message);
    } finally {
      setAnnSending(false);
    }
  };

  // ── toggle history row ──
  const toggleHistory = (id) => {
    if (!historyEnabled) setHistoryEnabled(true); // trigger orders fetch
    setExpandedHistory(expandedHistory === id ? null : id);
  };

  // ── derived display ──
  const filtered = customers.filter(c =>
    !search ||
    (c.business_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone||'').includes(search) ||
    (c.email||'').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    if ((a.last_order_at||'') > (b.last_order_at||'')) return -1;
    if ((a.last_order_at||'') < (b.last_order_at||'')) return 1;
    return (a.business_name || a.contact_name || '').localeCompare(b.business_name || b.contact_name || '');
  });

  const fromOrderCount = customers.filter(c => c.source === 'order').length;
  const leadCount       = customers.filter(c => c.source === 'approved_lead').length;

  if (isLoading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
      {lang === 'ar' ? 'جاري التحميل…' : 'Loading…'}
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--sedra-forest-soft)' }}>{t.customers}</h2>
          <div style={{ fontSize: 12, color: 'var(--sedra-sage-ink)', marginTop: 3 }}>
            {customers.length} total · {fromOrderCount} {lang === 'ar' ? 'مشتري' : 'buyers'} · {leadCount} {lang === 'ar' ? 'عميل محتمل' : 'leads'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={exportCustomers} style={{ ...btnStyle('#6b7280'), display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="Download" size={13} /> {lang === 'ar' ? 'تصدير CSV' : 'Export CSV'}</button>
          <button onClick={() => setAnnModal(true)} style={{ ...btnStyle('var(--sedra-olive)'), display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="Megaphone" size={13} /> {lang === 'ar' ? 'إرسال إعلان' : 'Send Announcement'}</button>
          <button onClick={() => setForm({ ...blank })} style={btnStyle()}>{t.addCustomer}</button>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, fontSize: 13 }} />
      </div>

      {msg && <div style={{ padding: '10px 16px', background: '#c6f6d5', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{msg}</div>}

      {/* ── Add / Edit Form ── */}
      {form && (
        <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? 14 : 24, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--sedra-forest-soft)' }}>{form.contact_name ? 'Edit Customer' : 'Add Customer'}</h3>
          {/* Contact edits sync across the CRM row, the account and the
              account's primary contact (DB triggers, 2026-08-01), so this form
              is a real edit point rather than a marketing-only copy. */}
          {form.retailer_application_id && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, lineHeight: 1.6, color: 'var(--sedra-sage-ink)', background: 'var(--sedra-sage-wash)', padding: '9px 12px', borderRadius: 8, marginBottom: 16 }}>
              <Icon name="Users" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {lang === 'ar'
                  ? 'مرتبط بحساب جملة — تعديل الاسم أو البريد أو الهاتف هنا يُحدِّث الحساب وجهة الاتصال الأساسية أيضاً (رسائل الطلبات ورمز التحقق).'
                  : 'Linked to a wholesale account — editing the name, email or phone here also updates the account and its primary contact (order emails and the verification code).'}
              </span>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {[[t.businessName, 'business_name'], [t.contactName, 'contact_name'], [t.phone, 'phone'], [t.whatsapp, 'whatsapp'], [t.email, 'email'], [t.address, 'address'], [t.notes, 'notes']].map(([label, key]) => (
              <div key={key} style={{ gridColumn: key === 'address' || key === 'notes' ? '1/-1' : undefined }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
                {key === 'notes' ? (
                  <textarea style={{ ...inputStyle, minHeight: 60 }} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                ) : (
                  <input style={inputStyle} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={save} style={btnStyle()}>{t.save}</button>
            <button onClick={() => setForm(null)} style={btnStyle('#888')}>{t.cancel}</button>
          </div>
        </div>
      )}

      {/* ── Customer List ── */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>{t.noCustomers}</div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map(c => (
            <div key={c.id} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--sedra-forest)' }}>{c.business_name || c.contact_name}</span>
                    {c.source === 'order'
                      ? <span style={{ fontSize: 10, background: 'var(--sedra-sage-wash)', color: '#3a7d1e', borderRadius: 8, padding: '2px 7px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="ShoppingCart" size={10} /> {lang === 'ar' ? 'مشتري' : 'Buyer'}</span>
                      : c.source === 'approved_lead'
                        ? <span style={{ fontSize: 10, background: 'var(--status-info-bg)', color: '#1d4ed8', borderRadius: 8, padding: '2px 7px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="Sparkles" size={10} /> {lang === 'ar' ? 'عميل محتمل' : 'Lead'}</span>
                        : null}
                  </div>
                  {c.business_name && c.contact_name && c.business_name !== c.contact_name && (
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>{c.contact_name}</div>
                  )}
                  <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{c.phone}{c.email && ` · ${c.email}`}</div>
                  {c.created_at && <div style={{ fontSize: 11, color: '#bbb', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="Clock" size={11} /> {new Date(c.created_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' })}</div>}
                  {c.last_order_at && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{t.lastOrder}: {new Date(c.last_order_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' })}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginLeft: 8, alignItems: 'flex-end' }}>
                  <ViewAsCustomerLink token={findAccount(c)?.token} lang={lang} compact />
                  <button onClick={() => whatsappCustomer(c)} style={{ ...btnStyle('#25d366'), padding: '5px 9px', display: 'flex' }}><Icon name="MessageCircle" size={13} /></button>
                  <button onClick={() => setForm({ ...c })} style={{ ...btnStyle('#6c8ebf'), padding: '5px 9px', display: 'flex' }}><Icon name="Pencil" size={13} /></button>
                  <button onClick={() => del(c.id)} style={{ ...btnStyle('var(--status-danger)'), padding: '5px 9px', display: 'flex' }}><Icon name="Trash2" size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--sedra-cream)' }}>
              {[t.businessName, t.contactName, t.phone, t.email, t.customerSource, t.lastOrder, ''].map((h, i) => (
                <th key={i} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 700, color: '#666', fontSize: 12 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{sorted.map((c, i) => (
              <React.Fragment key={c.id}>
                <tr style={{ borderTop: '1px solid var(--sedra-line)', background: i % 2 === 0 ? '#fff' : '#fdfbf8' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--sedra-forest)' }}>{c.business_name || '—'}</div>
                    {c.created_at && (
                      <div style={{ fontSize: 10, color: '#bbb', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="Clock" size={10} /> {new Date(c.created_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' })}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '11px 14px', color: '#444' }}>{c.contact_name}</td>
                  <td style={{ padding: '11px 14px', color: '#555', fontSize: 12 }}>{c.phone}</td>
                  <td style={{ padding: '11px 14px', color: '#888', fontSize: 12 }}>{c.email || '—'}</td>
                  <td style={{ padding: '11px 14px' }}>
                    {c.source === 'order'
                      ? <span style={{ fontSize: 11, background: 'var(--sedra-sage-wash)', color: '#3a7d1e', borderRadius: 8, padding: '2px 8px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="ShoppingCart" size={11} /> {lang === 'ar' ? 'مشتري' : 'Buyer'}</span>
                      : c.source === 'approved_lead'
                        ? <span style={{ fontSize: 11, background: 'var(--status-info-bg)', color: '#1d4ed8', borderRadius: 8, padding: '2px 8px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Sparkles" size={11} /> {lang === 'ar' ? 'عميل محتمل' : 'Lead'}</span>
                        : <span style={{ fontSize: 11, background: 'var(--sedra-line)', color: '#888', borderRadius: 8, padding: '2px 8px' }}>{lang === 'ar' ? 'يدوي' : 'Manual'}</span>}
                  </td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: '#aaa' }}>
                    {c.last_order_at ? new Date(c.last_order_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' }) : '—'}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <ViewAsCustomerLink token={findAccount(c)?.token} lang={lang} compact />
                      <button onClick={() => whatsappCustomer(c)} style={{ ...btnStyle('#25d366'), display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="MessageCircle" size={13} /> WA</button>
                      {c.email && (
                        <button onClick={() => window.open(`mailto:${c.email}`)} style={{ ...btnStyle('#3b82f6'), display: 'flex' }}><Icon name="Mail" size={13} /></button>
                      )}
                      <button onClick={() => toggleHistory(c.id)}
                        style={{ ...btnStyle(expandedHistory === c.id ? 'var(--sedra-olive)' : '#9ca3af'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Icon name="ClipboardList" size={13} /> {lang === 'ar' ? 'الطلبات' : 'Orders'}
                      </button>
                      <button onClick={() => setForm({ ...c })} style={{ ...btnStyle('#6c8ebf'), display: 'flex' }}><Icon name="Pencil" size={13} /></button>
                      <button onClick={() => del(c.id)} style={{ ...btnStyle('var(--status-danger)'), display: 'flex' }}><Icon name="Trash2" size={13} /></button>
                    </div>
                  </td>
                </tr>
                {expandedHistory === c.id && (() => {
                  const cOrders = orders.filter(o =>
                    (o.phone && o.phone === c.phone) ||
                    (o.email && o.email === c.email) ||
                    (o.business_name && o.business_name === c.business_name)
                  ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                  return (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 14px 14px', background: '#f8f6f2' }}>
                        <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e8e3da' }}>
                          {cOrders.length === 0 ? (
                            <div style={{ padding: '12px 16px', fontSize: 13, color: '#aaa' }}>{lang === 'ar' ? 'لا توجد طلبات لهذا العميل' : 'No orders found for this customer'}</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead><tr style={{ background: '#eef4ea' }}>
                                <th style={{ padding: '6px 12px', textAlign: 'left', color: 'var(--sedra-forest-soft)', fontWeight: 700 }}>Order ID</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', color: 'var(--sedra-forest-soft)', fontWeight: 700 }}>{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', color: 'var(--sedra-forest-soft)', fontWeight: 700 }}>{lang === 'ar' ? 'المنتجات' : 'Items'}</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', color: 'var(--sedra-forest-soft)', fontWeight: 700 }}>{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                              </tr></thead>
                              <tbody>
                                {cOrders.map((ord, oi) => (
                                  <tr key={ord.id} style={{ borderTop: '1px solid var(--sedra-line)', background: oi % 2 === 0 ? '#fff' : 'var(--sedra-sage-wash)' }}>
                                    <td style={{ padding: '7px 12px', fontFamily: 'monospace', color: '#666', fontSize: 11 }}>{ord.id}</td>
                                    <td style={{ padding: '7px 12px', color: '#888' }}>{ord.created_at ? new Date(ord.created_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' }) : '—'}</td>
                                    <td style={{ padding: '7px 12px', color: '#444' }}>{Array.isArray(ord.items) ? ord.items.map(it => `${it.name_en} ×${it.qty}`).join(', ') : '—'}</td>
                                    <td style={{ padding: '7px 12px' }}>
                                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700, background: ord.status==='new'?'var(--status-danger-bg)': ord.status==='delivered'?'var(--status-success-bg)':'var(--sedra-sage-wash)', color: ord.status==='new'?'var(--status-danger)': ord.status==='delivered'?'#166534':'var(--sedra-sage-ink)' }}>
                                        {ord.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </React.Fragment>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* ── Announcement Modal ── */}
      {annModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 20 : 32, width: '100%', maxWidth: 520, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 18, color: 'var(--sedra-forest-soft)', letterSpacing: lang === 'ar' ? 'normal' : '-0.01em', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="Megaphone" size={17} /> {lang === 'ar' ? 'إرسال إعلان' : 'Send Announcement'}
            </h3>
            <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--sedra-sage-ink)' }}>
              {lang === 'ar'
                ? `سيتم الإرسال إلى ${recipientCount} عميل مشترك`
                : `This will be sent to ${recipientCount} subscribed customer${recipientCount !== 1 ? 's' : ''}`}
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 5, fontWeight: 600 }}>
                {lang === 'ar' ? 'موضوع البريد الإلكتروني' : 'Email Subject'}
              </label>
              <input
                value={annSubject}
                onChange={e => setAnnSubject(e.target.value)}
                placeholder={lang === 'ar' ? 'أدخل موضوع الإعلان…' : 'Enter announcement subject…'}
                style={{ ...inputStyle }}
                disabled={annSending}
              />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 5, fontWeight: 600 }}>
                {lang === 'ar' ? 'نص الرسالة' : 'Message Body'}
              </label>
              <textarea
                value={annBody}
                onChange={e => setAnnBody(e.target.value)}
                placeholder={lang === 'ar' ? 'أدخل محتوى الإعلان…' : 'Enter your message…'}
                rows={6}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 120, fontFamily: 'inherit' }}
                disabled={annSending}
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setAnnModal(false); setAnnSubject(''); setAnnBody(''); }}
                style={btnStyle('#888')}
                disabled={annSending}
              >
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={sendAnnouncement}
                disabled={annSending || !annSubject.trim() || !annBody.trim() || recipientCount === 0}
                style={{
                  ...btnStyle(annSending || recipientCount === 0 ? '#aaa' : 'var(--sedra-olive)'),
                  opacity: (annSending || !annSubject.trim() || !annBody.trim() || recipientCount === 0) ? 0.65 : 1,
                  cursor:  (annSending || recipientCount === 0) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <Icon name={annSending ? 'Loader2' : 'Send'} size={13} />
                {annSending
                  ? (lang === 'ar' ? 'جاري الإرسال…' : 'Sending…')
                  : (lang === 'ar' ? 'إرسال' : 'Send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
